import type { ClintplayConfig, NetworkEventData } from './types';

const ALWAYS_DENY_RESPONSE_HEADERS = ['set-cookie'];

// Input types that may carry PII even when maskAllInputs is off.
const SENSITIVE_INPUT_TYPES = new Set(['password', 'email', 'tel']);

interface PrivacyOptions {
  networkSanitize?: ClintplayConfig['networkSanitize'];
  blockSelector?: string;
  maskAllInputs?: boolean;
  maskInputSelector?: string;
}

export class PrivacyFilter {
  constructor(private options: PrivacyOptions) {}

  shouldBlockElement(el: Element): boolean {
    const selector = this.options.blockSelector;
    if (!selector) return false;
    return el.matches(selector);
  }

  shouldMaskInput(el: HTMLInputElement): boolean {
    if (el.type === 'password') return true;
    if (this.options.maskAllInputs) return true;
    if (SENSITIVE_INPUT_TYPES.has(el.type)) return true;
    const selector = this.options.maskInputSelector;
    if (selector && el.matches(selector)) return true;
    return false;
  }

  sanitizeNetworkEvent(event: NetworkEventData): NetworkEventData {
    const result = { ...event };
    const config = this.options.networkSanitize;

    // Always remove sensitive response headers
    if (result.responseHeaders) {
      result.responseHeaders = this.filterHeaders(
        result.responseHeaders,
        ALWAYS_DENY_RESPONSE_HEADERS
      );
    }

    if (!config) return result;

    // Remove denied request headers
    if (result.requestHeaders && config.denyHeaders.length > 0) {
      result.requestHeaders = this.filterHeaders(
        result.requestHeaders,
        config.denyHeaders
      );
    }

    // Remove denied response headers
    if (result.responseHeaders && config.denyHeaders.length > 0) {
      result.responseHeaders = this.filterHeaders(
        result.responseHeaders,
        config.denyHeaders
      );
    }

    // Redact body for denied URLs
    if (config.denyBodyUrls.length > 0 && this.urlMatchesDeny(event.url, config.denyBodyUrls)) {
      if (result.requestBody) result.requestBody = '[REDACTED]';
      if (result.responseBody) result.responseBody = '[REDACTED]';
    }

    return result;
  }

  private filterHeaders(
    headers: Record<string, string>,
    denyList: string[]
  ): Record<string, string> {
    const denyLower = denyList.map((h) => h.toLowerCase());
    const filtered: Record<string, string> = {};
    for (const [key, value] of Object.entries(headers)) {
      if (!denyLower.includes(key.toLowerCase())) {
        filtered[key] = value;
      }
    }
    return filtered;
  }

  private urlMatchesDeny(url: string, patterns: string[]): boolean {
    return patterns.some((pattern) => {
      const regex = new RegExp(
        '^' + pattern.replace(/\*/g, '.*') + '$'
      );
      return regex.test(url);
    });
  }
}
